<template>
    <div id="project-navigator-container" ref="ProjectTree">
        <div id="app-layout-right-column__content__title">
            <span>Media</span>
        </div>

        <template v-if="!initialProjectTreeRefresh">
            <div v-if="projectTree.length > 0" id="project-root-container">
                <ImportMedia />
                <v-icon size="20" color="#ababab" class="material-icons ml-auto" @click="deleteAllMedia()" v-tippy
                        content="Delete all media">highlight_off
                </v-icon>
            </div>

            <ImportMedia v-else initial
                         style="width: 100%; position: absolute; left: 50%; transform: translateX(-50%)" />
        </template>

        <div class="scrollable-con"
             v-if="projectTree.length > 0"
        >
            <v-treeview
                id="project-tree"
                v-model="selectedFiles"
                :items="projectTree"
                dense
                open-on-click
                selectable
                :selection-type="'independent'"
                item-key="id"
                return-object
                ref="projectTree"
                name="project-tree"
            >
                <template v-slot:prepend="{ item, open }">
                    <div @click="onMediaSelection($event, item)"
                         class="thumbnail-container"
                         :data-thumbnail-container="item.id"
                         :class="{
                        '--edited': item.IDT && !item.IDT.initial,
                        '--selected': activeImage == item.id,
                    }"
                    >
                        <v-lazy>
                            <FileThumbnail :item="item" />
                        </v-lazy>
                    </div>
                </template>

                <template v-slot:label="{ item }">
                    <div class="file">
                    <span class="file-name"
                          @click="onMediaSelection($event, item)"
                          :class="{ '--selected': activeImage == item.id }"
                    >
                        {{ stripTimestamp(item.blob.name) }}
                    </span>

                        <FileTags :file="item" />
                    </div>
                </template>

                <template v-slot:append="{ item }">
                    <div class="menu-container">
                        <v-menu ref="menu" left nudge-left="20px" :content-class="'file-options-container'" :disabled="blockLoading">
                            <template v-slot:activator="{ on, attrs }">
                                <i class="material-icons" v-bind="attrs" :file_id="item.id" v-on="on">more_vert</i>
                            </template>
                            <v-list>
                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canUseColorGradingTools">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-symbols-outlined">ambient_screen</i>
                                            <v-list-item-title>NeuralFilmAI <span class="control-label--new">NEW</span></v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="modelName in neuralFilmAIModelNamesList"
                                            :key="modelName"
                                            :class="{ '--selected': false }"
                                            @click="neuralFilmAIHandler(modelName, item)"
                                        >
                                            <v-list-item-title>{{ modelName }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'media'"
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canUseColorGradingTools">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-symbols-outlined">ambient_screen</i>
                                            <v-list-item-title>AI Auto Correct <span class="control-label--new">NEW</span></v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item :class="{ '--selected': false }"
                                                     @click="AIAutoCorrectV2Handler('favor-context', item)"
                                        >
                                            <v-list-item-title>Favor Context</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item :class="{ '--selected': false }"
                                                     @click="AIAutoCorrectV2Handler('favor-neutrality', item)"
                                        >
                                            <v-list-item-title>Favor Neutrality</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-list-item
                                    @click="downloadLUT(item)"
                                    :disabled="firstSwap || !canExportLUT"
                                >
                                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
                                    <v-list-item-title>Download a LUT</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    @click="downloadXMP(item)"
                                    :disabled="firstSwap || !canExportXMP"
                                >
                                    <img src="/assets/icons/lr-icon-96x96.png" />
                                    <v-list-item-title>Download XMP Profile</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    @click="downloadCO(item)"
                                    :disabled="firstSwap || !canExportCO"
                                >
                                    <img src="/assets/icons/co-icon-96x96.png" />
                                    <v-list-item-title>Download Capture One Style</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'media'"
                                             @click="compareView(item)"
                                             :disabled="selectedFiles.length <= 1"
                                >
                                    <i class="material-icons">photo</i>
                                    <v-list-item-title>Compare View</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'media'" @click="applyGradeFrom(item)"
                                             :disabled="!canUseColorGradingTools || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-icons">perm_media</i>
                                    <v-list-item-title>Apply Grade From...</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'media'" @click="openAIColourMatchModalV2(item)"
                                             :disabled="firstSwap || selectedFiles.length > 1 || !canUseColorGradingTools || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-icons">auto_awesome</i>
                                    <v-list-item-title style="gap: 10px;">AI Colour Match To... <span
                                        class="control-label--new">NEW</span></v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'media'" @click="saveAllAsCandidates(item)"
                                             :disabled="!canCreateAssets || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-symbols-outlined">difference</i>
                                    <v-list-item-title>Save as Candidate</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator"
                                                     v-bind="attrs"
                                                     v-on="on"
                                                     :disabled="!canAssignTagToAsset"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">star_rate</i>
                                            <v-list-item-title>Rating</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="ratingTag in tags.rating"
                                            :key="ratingTag.value"
                                            class="stars-icons-container"
                                            :class="{ '--selected': fileHasTag(item, 'rating', ratingTag.value) }"
                                            @click="addTag(item, 'rating', ratingTag.value)"
                                        >
                                            <v-list-item-title v-if="ratingTag.value == 'none'">{{
													ratingTag.label
                                                }}
                                            </v-list-item-title>
                                            <i v-else v-for="index in ratingTag.value" :key="index"
                                               class="material-icons">star_rate</i>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="!canAssignTagToAsset">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">fiber_manual_record</i>
                                            <v-list-item-title>Status</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="statusTag in tags.status"
                                            :key="statusTag.value"
                                            :class="{ '--selected': fileHasTag(item, 'status', statusTag.value) }"
                                            @click="addTag(item, 'status', statusTag.value)"
                                        >
                                            <i class="material-icons" :style="{ color: statusTag.styleColor }">fiber_manual_record</i>
                                            <v-list-item-title>{{ statusTag.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="!canAssignTagToAsset">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">label</i>
                                            <v-list-item-title>Tag</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="colorTag in tags.color"
                                            :key="colorTag.value"
                                            :class="{ '--selected': fileHasTag(item, 'color', colorTag.value) }"
                                            @click="addTag(item, 'color', colorTag.value)"
                                        >
                                            <i class="material-icons" :style="{ color: colorTag.styleColor }">label</i>
                                            <v-list-item-title>{{ colorTag.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'media'"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --overflow --level2 --IDT'"
                                    :close-on-content-click="false"
                                    :max-height="390"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="firstSwap || !canUseColorGradingTools"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">adjust</i>
                                            <v-list-item-title>ACES Input Transform</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="IDT in IDTList"
                                            :key="IDT.value"
                                            class="full-span"
                                            :class="{
											'--selected': fileOptions.IDT && fileOptions.IDT.value == IDT.value && !fileOptions.IDT.initial,
											'--first': IDT.label == 'No Input Transform',
											'hidden': IDT.hidden
										}"
                                            @click="onIDTODTselection(item, 'IDT', IDT)"
                                        >
                                            <v-list-item-title>{{ IDT.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'media'"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --overflow --level2 --ODT'"
                                    :close-on-content-click="false"
                                    :max-height="390"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="firstSwap || !canUseColorGradingTools"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">adjust</i>
                                            <v-list-item-title>ACES Output Transform</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="ODT in ODTList"
                                            :key="ODT.value"
                                            class="full-span"
                                            :class="{
											'--selected': fileOptions.ODT && fileOptions.ODT.value == ODT.value && !fileOptions.ODT.initial,
											'--first': ODT.label == 'No Output Transform',
											'hidden': ODT.hidden
										}"
                                            @click="onIDTODTselection(item, 'ODT', ODT)"
                                        >
                                            <v-list-item-title>{{ ODT.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-list-item @click="deleteMedia(item)">
                                    <v-icon class="material-icons">mdi-delete</v-icon>
                                    <v-list-item-title>Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </template>
            </v-treeview>
        </div>


<!--        <tippy-->
<!--            v-if="startTipsStep == 2 && projectTree.length > 0"-->
<!--            placement="right"-->
<!--            boundary="window"-->
<!--            theme="&#45;&#45;start"-->
<!--            showOnInit="true"-->
<!--            hideOnClick="false"-->
<!--            interactive-->
<!--            :onHide="function() { return false }"-->
<!--            to="project-tree"-->
<!--        >-->
<!--            <span class="step">{{ startTipsStep }} of {{ $startTips.length }}</span>-->
<!--            <span class="title">{{ $startTips[startTipsStep - 1].title }}</span>-->
<!--            <span class="description">{{ $startTips[startTipsStep - 1].description }}</span>-->
<!--        </tippy>-->
    </div>
</template>

<script>
import CanMixin from "@/mixins/CanMixin";
import { mapActions, mapState } from 'vuex';
import FilenameMixin from '@/mixins/Filename';
import FileThumbnail from '@/components/app/project-tree/ProjectTreeFileThumbnail.vue';
import FileTags from "@/components/app/files/FileTags.vue";
import ImportMedia from "@/components/app/buttons/ImportMedia.vue";
import { cloneDeep } from "lodash";
import { stripExtension, stripTimestampAndExtension } from "@/assets/js/Utilities";


export default {
    name: 'ProjectTree',
    mixins: [FilenameMixin, CanMixin],
    components: {
        ImportMedia,
        FileThumbnail,
        FileTags
    },

    data() {
        return {
            switchingImageInterval: false,
            firstSwap: true,
            selectedFiles: [],
            fileOptions: {
                IDT: null,
                ODT: null,
            },
        };
    },
    computed: {
        ...mapState('File/Media', ['active', 'tags']),
        ...mapState('UI', ['startTipsStep']),
        ...mapState('Projects', ['project', 'projectTree', 'initialProjectTreeRefresh', 'appliedFilters']),
        ...mapState("App", ["IDTList", "ODTList", "blockLoading"]),
        ...mapState('Projects', {
            activeImage: 'image',
            openProjectTree: 'openProjectTree',
        }),
        neuralFilmAIModelNamesList() {
            return [
                'Generic Film Stock',
                'F-Stock 3513',
                'F-Stock 3510',
                'K-Stock 2393',
                'K-Stock 2395',
            ]
        }
    },
    created() {
        this.$root.projectTree = this;
    },
    mounted() {
        this.$root.projectTreeComponent = this.$refs.projectTree;
        this.$store.dispatch('Projects/refreshProjectTree');
    },
    methods: {
        ...mapActions('App', ['applyGrade']),
        ...mapActions('Helpers', ['revokeObjectURL']),

        nicename(name, stripTimestampOnly = false) {
            if (stripTimestampOnly) return this.stripTimestamp(name);
            else return this.stripTimestampAndExtension(name);
        },

        async onIDTODTselection(file, selectionType, values) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            await window.lutify.refreshLastStates(selectedFiles);
            let updateRemoteLastStates = [];
            let updateStoreLastStates = [];
            let timestamp = Date.now();
            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()));

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;
                let ODT = null;

                // IDT-ODT combinations
                if (selectionType == "IDT" && isIDTInitial) {
                    let ODTname = window.lutify.getODTtitleFromCombination(null, values.value);
                    ODT = window.lutify.getODTbyName(ODTname);
                }

                imageState.blocks.forEach(block => {
                    if (block.name == selectionType) {
                        block.values = values;
                        if (selectionType == "IDT") block.gamutCompress = true;
                    }
                    if (ODT && block.name == "ODT") block.values = ODT;
                });

                let lastStateData = {
                    timestamp,
                    blocks: imageState.blocks,
                };

                // update UI
                if (selectionType == "IDT") this.fileOptions.IDT = values;
                if (selectionType == "ODT" || ODT) this.fileOptions.ODT = ODT ? ODT : values;

                updateRemoteLastStates.push({
                    image_id: selectedFile.id,
                    lastStateData,
                });

                updateStoreLastStates.push({
                    key: selectedFile.id.toString(),
                    value: lastStateData,
                });
            }

            await window.imagesStateStore.setItems(updateStoreLastStates);
            await this.$store.dispatch("Projects/refreshProjectTree");

            let activeImage = this.$store.state.Projects.image;
            if (selectedFiles.map(e => e.id).includes(activeImage)) {
                await window.lutify.applyLastState(activeImage);
                // await this.updateFileThumbnail(
                //     selectedFiles.find(file => file.id == activeImage),
                //     await window.lutify.fetchLastState(activeImage)
                // );
                this.$store.dispatch("Projects/refreshProjectTree");
            }

            // window.lutify.postLastStates(updateRemoteLastStates);
        },

        async downloadLUT(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let selectedFiles = this.selectedFiles;
            // if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "media") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const {blocks} = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            } else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-lut-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-lut')
        },

        async downloadXMP(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let selectedFiles = this.selectedFiles;
            // if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "media") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const {blocks} = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            } else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-xmp-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
        },

        async downloadCO(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "image") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const {blocks} = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            }
            else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-co-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
        },

        openColourMatchModal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.colourMatch.el);
        },

        openColourMatchV2Modal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.colourMatchV2.el);
        },

        openAdvancedColourMatchV2Modal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.advancedColourMatchV2.el);
        },

        applyGradeFrom(file) {
            if (!this.selectedFiles.map(e => e.id).includes(file.id)) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action: "applyGradeFrom",
                    id: file.id,
                    data: [file],
                });
            } else {
                this.$store.dispatch("Projects/setAction", {
                    type: this.selectedFiles[0].type,
                    action: "applyGradeFrom",
                    id: this.selectedFiles[0].id,
                    data: this.selectedFiles,
                });
            }

            this.$store.dispatch("Projects/setApplyGradeFromCandidateProjectTree");
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.grade.applyFrom);

            // v-tab active fix
            document.querySelectorAll("#apply-grade-from-modal .v-tab")[1].click();
            setTimeout(() => document.querySelectorAll("#apply-grade-from-modal .v-tab")[0].click(), 10);
        },

        async openAIColourMatchModal(file, action = "AIColourMatchFrom") {
            if (await _Adjustments.AIColourMatch.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourMatch.el);
            } else {
                _Adjustments.AIColourMatch.loadModel();
            }
        },

        async openAIColourMatchModalV2(file, action = "AIColourMatchFrom") {
            if (await _Adjustments.AIColourMatchV2.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourMatchV2.el);
            } else {
                _Adjustments.AIColourMatch.loadModel();
            }
        },

        fileHasTag(file, tagName, value) {
            if (file.tags) return file.tags.find(tag => tag.name == tagName && tag.value == value);
            else return false;
        },

        async addTag(file, tag_name, tag_value) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (tag_name == "rating") {
                let sameTagOnAll = selectedFiles
                    .map(e => {
                        if (e.tags.find(tag => tag.name == tag_name)) return e.tags.find(tag => tag.name == tag_name).value;
                    })
                    .every(value => tag_value == value);
                if (sameTagOnAll) {
                    await this.$store.dispatch("File/Media/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    // NOTE: can be implemented in back-end to auto switch to none
                    await this.$store.dispatch("File/Media/addTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value: "none",
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            if (tag_name == "color") {
                let sameTagOnAll = selectedFiles
                    .map(e => e.tags.filter(tag => tag.name == "color").map(tag => tag.value))
                    .every(tag => tag.includes(tag_value));

                if (sameTagOnAll) {
                    await this.$store.dispatch("File/Media/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            await this.$store.dispatch("File/Media/addTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name,
                tag_value,
            });

            await this.$store.dispatch("Projects/refreshProjectTree");
            // this.refreshSelectedFiles();
        },

        startFileRenaming(file) {
            this.$store.dispatch('Projects/setAction', {
                type: 'file',
                action: 'file-rename',
                data: file,
            });

            this.renamingFileKey = file.key;
            this.renamingFileName = this.nicename(file.name);
        },
        async renameFile(e) {
            if (e.key == 'Enter' || e.keyCode == 13) {
                this.renamingFileKey = null;

                let oldFilename = this.$store.state.Projects.action.data.name;
                let fileType = oldFilename.substring(oldFilename.lastIndexOf('.') + 1);
                let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

                if (this.nicename(oldFilename) !== this.renamingFileName) {
                    await this.$store.dispatch('Projects/updateFileName', {
                        name: newFilename,
                    });

                    if (this.$store.state.Projects.action.data.type == 'match') {
                        await this.$store.dispatch('UI/renameMatch', {
                            id: this.$store.state.Projects.action.data.id,
                            name: newFilename,
                            nicename: this.stripTimestampAndExtension(newFilename),
                        });

                        let matchesStoreData = await matchesStore.getItem(this.$store.state.Projects.action.data.id);
                        if (matchesStoreData)
                            matchesStore.setItem(matchesStoreData.id, {
                                ...matchesStoreData,
                                name: newFilename,
                                nicename: this.renamingFileName,
                            });
                    }

                    if (this.$store.state.Projects.action.data.type == 'candidate') {
                        await this.$store.dispatch('UI/renameCandidate', {
                            id: this.$store.state.Projects.action.data.id,
                            name: newFilename,
                            nicename: this.stripTimestampAndExtension(newFilename),
                        });

                        let candidateStoreData = await candidatesStore.getItem(
                            this.$store.state.Projects.action.data.id);
                        if (candidateStoreData)
                            candidatesStore.setItem(candidateStoreData.id, {
                                ...candidateStoreData,
                                name: newFilename,
                                nicename: this.renamingFileName,
                            });
                    }

                    if (this.$store.state.Projects.action.data.type == 'image' || this.$store.state.Projects.action.data.type == 'media') {
                        let imageStoreData = await imagesStore.getItem(this.$store.state.Projects.action.data.id);
                        if (imageStoreData) imagesStore.setItem(imageStoreData.id, {
                            ...imageStoreData,
                            name: newFilename,
                        });
                    }

                    await vm.$store.dispatch('Projects/refreshProjectTree', {
                        createPresignedURL: true,
                    });
                }

                this.endFileRenaming();
            } else if (e.key == 'Escape' || e.keyCode == 27) {
                this.endFileRenaming();
            }
        },
        endFileRenaming() {
            this.renamingFileKey = null;
            this.$store.dispatch('Projects/setAction', {
                type: 'file',
                action: 'file-rename-end',
                data: null,
            });
        },

        async deleteAllMedia() {
            window.UI.showModal(window.UI.modals.files.deleteAllMedia)
        },

        async deleteMedia(media) {
            let selectedFiles = this.selectedFiles;

            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(media.id)) selectedFiles = [media];

            for await (const media of selectedFiles) {
                await this.$store.dispatch('File/Media/delete', media);
            }
        },

        async onMediaSelection($event, file) {
            if (this.$store.state.App.blockLoading) return;
            if (this.switchingImageInterval) return;

            this.switchingImageInterval = true;
            setTimeout(() => (this.switchingImageInterval = false), 1000);

            if ($event.shiftKey && !$event.altKey && this.selectedFiles.length == 1) {
                let startFile = this.selectedFiles[0];
                let endFile = file;

                if (startFile.type !== endFile.type) return; // clicked file with another type
                const result = this.getInBetweenFileIds(this.projectTree, startFile, endFile);
                if (result) this.selectedFiles = result;
                return;
            }

            this.selectedFiles = [file];

            // this.$store.dispatch('UI/setStartTipsStep', 3);
            await this.$store.dispatch('File/Media/switch', file);
            this.switchImage(file);
        },

        async switchImage(file, applyingCandidate = false) {
            if (this.firstSwap) {
                window.UI.container.mainLayoutMiddleBottomArea.removeClass("hide");
                $("#wrapper").css("display", "flex");
            }

            if (!applyingCandidate) UI.appendFileLoadingSpinner(file.id);
            UI.showImageLoader();

            lutify.removeSplitView();
            loader.imageName = this.nicename(file.name);
            this.$store.dispatch("Projects/setActiveCandidate", null);

            let storeData = file;

            await loader.areRequiredFilesLoaded();
            if (!await loader.setActiveMedia(storeData.blob, storeData.fileType, file.id)) return;
            await loader.loadActiveMedia();

            // saving images current state, before updating select image object
            if (this.firstSwap) {
                lutify.initialBlocks = cloneDeep(lutify.getRawBlocksData());
            } else if (!this.$store.state.Projects.projectSwitch) {
                await lutify.storeLastState(null, null, loader.previousMedia);
            }

            if (loader.typeOfActiveMedia('raw')) {
                let imageState = await window.lutify.fetchLastState(file.id);
                if (imageState?.rawDevelopment?.develop) {
                    vm.rawDevelopment.setValues(imageState.rawDevelopment)
                } else if (imageState?.rawDevelopment?.colourSpace) {
                    // backwards compatibility
                    vm.rawDevelopment.setValues({
                        develop: true
                    })
                } else {
                    vm.rawDevelopment.resetValues();
                }
            } else {
                this.$store.dispatch('Projects/setExif', {width: null, height: null})
            }

            // updating selected image object
            this.$store.dispatch("Projects/setImage", file.id);
            if (!applyingCandidate) this.$store.dispatch("Projects/setCurrentFile", file);

            // applying last state
            await lutify.applyLastState(file.id);

            if (!applyingCandidate && !vm.rawDevelopment.updating) {
                UI.hideImageLoader();
                UI.removeFileLoadingSpinner(file.id);
            }

            if (loader.typeOfActiveMedia('raw')) {
                let imageState = await window.lutify.fetchLastState(file.id);
                if (imageState?.rawDevelopment?.colourSpace) window.UI.IDT.selectByName('ACES2065-1'); // backwards compatibility
                else if (!imageState?.rawDevelopment?.develop) window.UI.IDT.selectByName('sRGB');
            }

            if (this.firstSwap) this.firstSwap = false;

            lutify.switchCandidates();
            lutify.switchMatches();

            UI.toggleStartTips();
        },

        async compareView(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            this.$store.commit('UI/RESET_COMPARE_VIEW');
            UI.showModal(UI.modals.compareView.el);

            for await (const selectedFile of selectedFiles) {
                this.$store.commit('UI/ADD_LOADING_ASSET_TO_COMPARE_VIEW');

                let storeData;
                if (selectedFile.type == "candidate") {
                    storeData = await lutify.fetchCandidate(selectedFile);

                    if (!storeData.image) storeData.image = await lutify.fetchCandidateImage(selectedFile)
                } else if (selectedFile.type == "match") {
                    storeData = await lutify.fetchMatch(selectedFile);
                    storeData.image = storeData.blob;
                } else if (selectedFile.type == "image" || selectedFile.type == 'media') {
                    await this.switchImage(selectedFile);
                    let img = gpu.composer.renderer.domElement.toDataURL("image/jpeg", 1);
                    let file = loader.dataURLtoFile(img, 'media');
                    storeData = await window.lutify.fetchImage(selectedFile);
                    storeData.image = file;
                }

                storeData.tags = selectedFile.tags;
                storeData.loaded = true;
                storeData.assetObject = selectedFile;

                this.$store.commit('UI/ADD_ASSET_TO_COMPARE_VIEW', storeData);
            }
        },

        getInBetweenFileIds(files, startFile, endFile) {
            let startIndex = files.map(e => e.key).indexOf(startFile.key);
            let endIndex = files.map(e => e.key).indexOf(endFile.key);
            // if (!startIndex || !endIndex) return;
            return (
                files
                    // .map(fileItem => fileItem.id)
                    .filter((fileItem, index) => {
                        if (startIndex < endIndex) {
                            // shift-clicling down the list
                            if (index >= startIndex && index <= endIndex) return true;
                        } else {
                            // shift-clicking up the list
                            if (index >= endIndex && index <= startIndex) return true;
                        }
                    })
            );
        },

        async saveAllAsCandidates(file) {
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('Projects/RESET_SAVE_ALL_AS_CANDIDATES_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.saveAllAsCandidates.processing.el)
            // await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            // picking a new candidate name and checking against existing candidate names
            let newCandidateName = 1;
            let existingCandidatesList = this.$store.getters['File/Candidates/getAllCandidates'];
            existingCandidatesList.every(candidate => {
                if (existingCandidatesList.map(c => stripTimestampAndExtension(c.name).split('v')[1]).includes(newCandidateName.toString())) {
                    return newCandidateName++;
                }
            })

            newCandidateName = 'v' + newCandidateName;

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState?.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_SAVE_ALL_AS_CANDIDATES_UNPROCESSED_FILE' +
                        '', selectedFile);
                    continue;
                }

                this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await this.switchImage(selectedFile);
                await window.lutify.addCandidate(newCandidateName, false);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', '');
            UI.hideModal()

            if (this.$store.state.Projects.saveAllAsCandidatesUnprocessedList.length > 0) {
                UI.showModal(UI.modals.saveAllAsCandidates.unprocessedList.el);
            }
        },

        async neuralFilmAIHandler(modelName, file) {
            // TODO do we need this line?
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_STEP', 1)
            this.$store.commit('Projects/RESET_NEURAL_FILM_AI_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.neuralFilmAI.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_NEURAL_FILM_AI_UNPROCESSED_FILE', selectedFile);
                    continue;
                }

                await this.switchImage(selectedFile);

                this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await window._Adjustments.NeuralFilmAI.addBlock(undefined, {
                    initial: true,
                    multiProcess: true,
                    filesToProcess: selectedFiles
                });

                this.$root.$refs.addControlButtonsContainer._tippy.hide();

                let modelIndex = window._Adjustments.NeuralFilmAI.modelNamesList.indexOf(modelName);
                let $preview = $(`#previews-list-container .list-item[data-category="NeuralFilmAI"][data-preset-id="${modelIndex}"]`)

                lutPreviewer.onColourMatchPreviewSelection($preview)

                let preset = luts['LUTcontrols']['NeuralFilmAI'][modelIndex]
                lutify.getActiveBlock().presetId = modelIndex.toString()
                await window._Adjustments.NeuralFilmAI.onPresetSelection(preset)
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');
            UI.hideModal()

            if (this.$store.state.Projects.neuralFilmAIUnprocessedList.length > 0) {
                UI.showModal(UI.modals.neuralFilmAI.unprocessedList.el);
            }
        },

        async AIAutoCorrectV2Handler(model, file) {
            // TODO do we need this line?
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_STEP', 1)
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_MODAL_TITLE', 'AI Auto Correct')
            this.$store.commit('Projects/RESET_NEURAL_FILM_AI_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.neuralFilmAI.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_NEURAL_FILM_AI_UNPROCESSED_FILE', selectedFile);
                    continue;
                }

                await this.switchImage(selectedFile);

                this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', stripExtension(selectedFile.name));

                await window._Adjustments.AIAutoCorrectV2.addBlock(undefined, {
                    initial: true,
                    multiProcess: true,
                    filesToProcess: selectedFiles,
                    model
                });
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_MODAL_TITLE', '')
            UI.hideModal()

            if (this.$store.state.Projects.neuralFilmAIUnprocessedList.length > 0) {
                UI.showModal(UI.modals.neuralFilmAI.unprocessedList.el);
            }
        },

    },
};
</script>

<style lang="scss">
#project-navigator-container {
    display: grid;
    align-content: start;
    background: transparent;
    color: #ababab;
    font-size: 1rem !important;
    position: relative;
    height: 100%;

    #project-root-container {
        display: flex;
        align-items: center;
        min-height: 22px;
        padding: 0 6px;
        margin-bottom: 10px;

        > i {
            cursor: pointer;
        }
    }

    #import-media {
        display: none;
    }

    #project-tree {
        color: #ababab;
        overflow-y: scroll;

        // media item
        > .v-treeview-node {
            + .v-treeview-node--leaf {
                margin-top: 10px;
            }

            &.v-treeview-node--leaf {
                > .v-treeview-node__root {
                    &:before {
                        border-radius: 5px;
                    }
                }

                &[aria-expanded="false"] {
                    > .v-treeview-node__root {
                        &:before {
                            border-radius: 5px 5px 0 0;
                        }
                    }
                }
            }

            &[aria-expanded="false"] {
                > .v-treeview-node__root {
                    &:before {
                        border-radius: 0 0 5px 5px;
                    }
                }
            }

            &.v-treeview-node--selected {
                > .v-treeview-node__root {
                    &:before {
                        background: #212143;
                    }
                }
            }

            > .v-treeview-node__root {
                position: relative;
                padding-left: 20px;

                &:before {
                    content: "";
                    display: block;
                    position: absolute;
                    left: 0;
                    top: 0;
                    background: #202227;
                    width: 100%;
                    height: 100%;
                    opacity: 1;
                    right: initial;
                    bottom: initial;
                    transition: none;
                }

                > .v-treeview-node__level {
                    display: none;
                }
            }
        }

        .v-treeview-node__root:before {
            display: none;
        }

        .v-icon {
            color: #989898;
        }

        .v-treeview-node__checkbox {
            display: none;
        }
    }

    .v-treeview-node__label {
        line-height: 1.5;
        align-self: stretch;

        .v-input {
            font-size: 12px;

            .v-input__control {
                min-height: initial;
                // height: 10px;
                input {
                    line-height: 1;
                    color: var(--font-default-color);
                    padding: 0;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                }

                .v-input__slot {
                    padding: 0;
                    background: transparent;
                }
            }

            &--is-focused {
                .v-input__slot {
                    // background: #363636;
                    background: transparent;
                }
            }
        }
    }

    .file {
        position: relative;
        overflow: visible;
        display: flex;
        align-items: center;
        height: 100%;

        .loading-spinner-container {
            position: absolute;
            left: -40px;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    .file-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        line-height: 35px;
        // font-weight: bold;
        &.--selected {
            color: #ffffff;
            font-weight: bold;
        }
    }

    .v-treeview-node__root {
        min-height: 25px;

        padding: 0;

        &:hover .menu-container {
            opacity: 1;
        }
    }

    .menu-container {
        opacity: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .v-icon.v-icon:after {
        display: none;
    }

    .v-treeview-node__content {
        //margin-left: -3px;
        z-index: 1;
    }

    .v-treeview-node__prepend {
        margin-right: 0;
        margin-left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
    }

    .v-treeview-node__append {
        display: flex;
        //max-width: 18px;
        //min-width: initial;
    }

    i {
        font-size: 18px;
        cursor: pointer;
    }

    .thumbnail-container {
        width: 50px;
        height: 35px;
        margin-left: 4px;
        margin-right: 5px;

        &.--edited,
        &.--notify {
            &::before {
                content: "";
                display: block;
                position: absolute;
                top: 7px;
                left: -15px;
                width: 5px;
                height: 5px;
                background: var(--font-default-color);
                border-radius: 50%;
            }

            &.--selected::before {
                background: var(--primary);
            }
        }

        .file-thumbnail {
            width: 100%;
            height: 100%;
            max-height: 35px;
            object-fit: contain;
            padding: 3px 0;
            // border-radius: var(--thumbnail-sm-border-radius);
        }
    }

    .placeholder-container {
        height: 35px;
        padding: 3px 0;

        .placeholder-preview {
            height: 100%;
            border-radius: 1px;
        }
    }
}

.file-options-container,
.file-options-container--no-icons {
    z-index: 200 !important;
    border-radius: var(--box-rounding);

    &.--level2 {
        //min-width: initial !important;

        &.--rating {
            .v-list-item {
                display: flex !important;
                align-items: center;
            }

            .v-list-item__title {
                margin-left: 0 !important;
            }
        }
    }

    &.--overflow {
        padding: 25px 0;
        background: var(--dark-gray);

        .v-list {
            height: 340px;
            padding: 0;
            overflow-y: scroll;
            position: relative;
            display: flex;
            flex-direction: column;

            scrollbar-color: #989898 transparent;
            scrollbar-width: thin;

            &::-webkit-scrollbar {
                width: 5px;
            }

            &::-webkit-scrollbar-thumb {
                background-color: #989898;
                border-radius: 50px;
            }
        }
    }

    .v-list {
        background: var(--dark-gray);
        padding: 25px 0;

        .v-list-item {
            min-height: 20px;
            padding: 0 25px;
            display: grid;
            grid-template-columns: 18px 1fr auto auto;
            cursor: pointer;
            position: relative;

            &.sub-menu-activator {
                grid-template-columns: 18px 1fr auto;
            }

            ~ .v-list-item {
                margin-top: 2px;
            }

            i:last-of-type {
                //margin-left: 1rem;
            }

            &.--first {
                order: -1;
            }

            > * {
                z-index: 2;
            }

            &:before {
                display: none;
            }

            &:hover:after {
                content: '';
                position: absolute;
                display: flex;
                width: calc(100% - 20px);
                height: calc(100%);
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: rgb(54, 54, 54);
                border-radius: 5px;
                z-index: 1;
            }

            &.full-span {
                grid-template-columns: 1fr;
            }

            &.stars-icons-container {
                grid-template-columns: repeat(5, 1fr);
            }

            &[aria-expanded="true"] {
                //background: rgb(54, 54, 54);
            }

            &.--selected {
                .material-icons,
                .v-list-item__title {
                    color: var(--primary);
                }
            }

            &.v-list-item--disabled {
                opacity: 0.6;
            }

            .material-icons, .material-symbols-outlined {
                color: #989898;
                font-size: 18px;
                width: 18px;

                &.arrow-left {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                }
            }

            .v-list-item__title {
                margin-left: 4px;
                margin-right: 4px;
                color: #989898;
                font-size: 1rem;
                line-height: 2;
                display: flex;
                align-items: center;
            }

            &:after {
                display: none;
            }
        }
    }

    &--no-icons {
        .v-list {
            .v-list-item {
                grid-template-columns: 1fr;
            }
        }
    }

    .control-label--new {
        color: white;
        background: var(--color-accent);
        border-radius: 5px;
        padding: 3px 8px;
        font-size: 0.8rem;
        margin-left: auto;
    }
}
</style>
